import {
  isActiveSessionAuthGenerationCurrent,
  type ActiveSessionAuthGenerationSnapshot,
} from './activeSessionSync';
import {
  hasPortalCookieSession,
  readActivePortalAuthChannel,
  selectExactPortalBearerToken,
  ssoUserEndpoint,
  type SsoContext,
} from './ssoSession';

export type TerminalLogoutCause = 'session_hydration' | 'protected_request';

export interface TerminalLogoutIntent {
  context: SsoContext;
  generation: ActiveSessionAuthGenerationSnapshot;
  authorization?: string;
  credentialKey: string;
  cause: TerminalLogoutCause;
  recoveryEligible: boolean;
  isCurrent: () => boolean;
}

export type TerminalLogoutOutcome = 'handled' | 'superseded' | 'unavailable';
type LogoutHandler = (intent: TerminalLogoutIntent) => Promise<void | TerminalLogoutOutcome>;
const handlers = new Map<string, LogoutHandler>();
const inFlight = new Map<string, Promise<TerminalLogoutOutcome>>();
const handledErrors = new WeakSet<object>();
const unavailableErrors = new WeakMap<object, () => boolean>();
const contextKey = (context: SsoContext): string => `${context.site}:${context.entryType}`;

/** Keep a confirmed logout on its local form; do not rewrite a broker handoff. */
export const terminalLogoutDestination = (
  destination: string,
  loginPath: string,
  origin: string,
): string => {
  try {
    const target = new URL(destination, origin);
    const login = new URL(loginPath, origin);
    if (target.origin !== login.origin || target.pathname !== login.pathname) return destination;
    target.searchParams.set('sso_passive', 'checked');
    return `${target.pathname}${target.search}${target.hash}`;
  } catch {
    return destination;
  }
};

export const registerTerminalLogoutHandler = (
  context: SsoContext,
  handler: LogoutHandler,
): (() => void) => {
  const key = contextKey(context);
  handlers.set(key, handler);
  return () => { if (handlers.get(key) === handler) handlers.delete(key); };
};

export const markTerminalLogoutErrorHandled = (error: object): void => {
  handledErrors.add(error);
};

export const isTerminalLogoutErrorHandled = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && handledErrors.has(error);

export const markTerminalLogoutUnavailable = (error: object, isCurrent: () => boolean): void => {
  unavailableErrors.set(error, isCurrent);
};

export const isTerminalLogoutUnavailable = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && unavailableErrors.has(error);

export const isUnavailableLogoutCurrent = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null) return false;
  try { return unavailableErrors.get(error)?.() === true; } catch { return false; }
};

export const classifyTerminalLogoutCause = (
  context: SsoContext,
  requestUrl: unknown,
): TerminalLogoutCause => {
  const requestPath = typeof requestUrl === 'string'
    ? requestUrl.split(/[?#]/, 1)[0]
    : '';
  return requestPath === ssoUserEndpoint(context)
    ? 'session_hydration'
    : 'protected_request';
};

export const captureTerminalLogoutIntent = (
  generation: ActiveSessionAuthGenerationSnapshot,
  authorization: unknown,
  localStorage: Pick<Storage, 'getItem'>,
  sessionStorage?: Pick<Storage, 'getItem'>,
  requestUrl?: unknown,
  recoveryEligible = false,
): TerminalLogoutIntent | undefined => {
  const { context } = generation;
  const tabStorage = sessionStorage ?? { getItem: () => null };
  const bearer = typeof authorization === 'string'
    ? authorization.match(/^Bearer ([^\s]+)$/i)?.[1] ?? null
    : null;
  // An explicit unsupported Authorization header prevents cookie promotion on
  // the server. Its failure says nothing about the ambient cookie session.
  if (authorization !== undefined && authorization !== null && bearer === null) return undefined;
  const cookie = bearer === null && hasPortalCookieSession(context, localStorage, tabStorage);
  if (bearer === null && !cookie) return undefined;
  const principalKey = context.entryType === 'admin' ? 'site_admin' : 'auth_user';
  const principal = localStorage.getItem(principalKey);
  const clinic = context.entryType === 'admin' ? null : localStorage.getItem('auth_clinic');
  const cause = classifyTerminalLogoutCause(context, requestUrl);

  return {
    context,
    generation,
    authorization: bearer === null ? undefined : `Bearer ${bearer}`,
    // Kept only in process memory for single-flight identity, never logged/persisted.
    credentialKey: bearer === null ? 'cookie' : bearer,
    cause,
    recoveryEligible: recoveryEligible && cause === 'session_hydration',
    isCurrent: () => {
      try {
        if (!isActiveSessionAuthGenerationCurrent(generation, localStorage)) return false;
        if (localStorage.getItem(principalKey) !== principal
          || (context.entryType !== 'admin' && localStorage.getItem('auth_clinic') !== clinic)) return false;
        if (bearer !== null) {
          return selectExactPortalBearerToken(context, localStorage) === bearer;
        }
        const channel = readActivePortalAuthChannel(context.site, localStorage);
        return selectExactPortalBearerToken(context, localStorage) === null
          && (!channel || channel.entryType === context.entryType && channel.transport === 'cookie')
          && hasPortalCookieSession(context, localStorage, tabStorage);
      } catch {
        return false;
      }
    },
  };
};

/** The registered provider owns the existing Logout-button flow and its mutex. */
export const requestTerminalLogout = (intent: TerminalLogoutIntent): Promise<TerminalLogoutOutcome> => {
  if (!intent.isCurrent()) return Promise.resolve('superseded');
  const key = `${contextKey(intent.context)}:${intent.generation.generation}:${intent.credentialKey}`;
  const existing = inFlight.get(key);
  if (existing) return existing;
  const handler = handlers.get(contextKey(intent.context));
  if (!handler) return Promise.resolve('unavailable');
  const ownedIntent = {
    ...intent,
    isCurrent: () => handlers.get(contextKey(intent.context)) === handler && intent.isCurrent(),
  };

  const pending: Promise<TerminalLogoutOutcome> = Promise.resolve().then(async () => {
    if (!ownedIntent.isCurrent()) return 'superseded' as const;
    // Legacy handlers have no explicit result and retain their existing contract.
    return (await handler(ownedIntent)) ?? 'handled';
  }).finally(() => {
    if (inFlight.get(key) === pending) inFlight.delete(key);
  });
  inFlight.set(key, pending);
  return pending;
};
